import { Info } from "lucide-react";
import { ReactNode, useEffect, useId, useRef, useState } from "react";

import { Button } from "./button";
import { cn } from "./cn";

interface Props {
  // 아이콘 버튼의 읽는 이름 — 「무엇에 대한 안내」 인지.
  label: string;
  children: ReactNode;
  // 단추에 붙는다 — 뿌리는 contents 라 상자가 없어 여기에 주면 먹지 않는다.
  className?: string;
  // 끈 안내 — 눌러도 열리지 않는다(aria-disabled).
  disabled?: boolean;
}

/** 「i」 안내 — **부모가 `flex flex-wrap` 일 때만** 안내가 그 줄 아래 한 줄을 차지해 밑의 칸을 민다(뿌리가 contents) · 라벨·제목 바로 옆에 두고 label 은 「건설업 등록번호 안내」 처럼 무엇에 대한 안내인지 · 눌러 열고 Esc·바깥 누름으로 닫는다(호버로 열리지 않는다) · 닫혀도 aria-describedby 로 읽힌다 · 근거·출처는 children 안 `mt-1 block text-muted-foreground` 한 줄 · 꼭 읽어야 하는 경고는 여기 숨기지 말고 Notice·Banner 로 · 종이에는 안 나온다(print-hide). */
const InfoTip = ({ label, children, className, disabled = false }: Props) => {
  const [open, setOpen] = useState(false);
  const toggle = () => {
    if (!disabled) setOpen((v) => !v);
  };
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  return (
    <span ref={root} className="print-hide contents">
      {/* 날 button 이 아닌 span — 잠긴 편집기의 <fieldset disabled> 안에서도 안내는 열린다. */}
      <Button
        aria-controls={id}
        aria-describedby={id}
        aria-disabled={disabled || undefined}
        aria-expanded={open && !disabled}
        aria-label={label}
        className={cn("text-muted-foreground", className)}
        data-testid="info-tip"
        size="icon-xs"
        variant="ghost"
        asChild>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            // <label> 안에 있어도 클릭이 라벨 칸 활성화·부모 클릭으로 넘어가지 않게 끊는다.
            e.preventDefault();
            e.stopPropagation();
            toggle();
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            // 네이티브 단추처럼 Enter 는 누를 때(누르고 있는 반복은 무시) · Space 는 뗄 때 연다 — 여기선 스크롤만 막는다.
            e.preventDefault();
            e.stopPropagation();
            if (e.key === "Enter" && !e.repeat) toggle();
          }}
          onKeyUp={(e) => {
            if (e.key !== " ") return;
            e.preventDefault();
            e.stopPropagation();
            toggle();
          }}>
          <Info aria-hidden />
        </span>
      </Button>
      <span
        className="mt-1 w-full basis-full rounded-md border border-border bg-popover p-3 text-xs font-normal leading-relaxed text-popover-foreground"
        hidden={!open || disabled}
        id={id}
        role="note">
        {children}
      </span>
    </span>
  );
};

export default InfoTip;
